import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  OutlinedInput,
} from '@mui/material';

const getDefaultAfterDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
};

const ScheduledSearchDialog = ({
  open,
  onClose,
  onSave,
  initialData = null,
  categories = [],
  teamName = 'default',
}) => {
  const [schedule, setSchedule] = useState({
    daysInterval: initialData?.schedule?.daysInterval || 1,
  });

  const initialDeeperProcess = initialData?.settings?.deeperProcess ?? true;

  const [settings, setSettings] = useState({
    afterDate: initialData?.settings?.afterDate || getDefaultAfterDate(),
    deeperProcess: initialDeeperProcess,
    categories: initialData?.settings?.categories || ["all"],
    categoryType: initialData?.settings?.categoryType || 
      (initialData?.settings?.categories?.includes('all') ? 'all' : 'specific')
  });

  useEffect(() => {
    if (initialData) {
      setSchedule({
        daysInterval: initialData.schedule?.daysInterval || 1,
      });

      setSettings({
        afterDate: initialData.settings?.afterDate || getDefaultAfterDate(),
        deeperProcess: initialData.settings?.deeperProcess ?? true,
        categories: initialData.settings?.categories || ["all"],
        categoryType: initialData.settings?.categoryType || 
          (initialData.settings?.categories?.includes('all') ? 'all' : 'specific')
      });
    }
  }, [initialData, teamName]);

  const handleSave = () => {
    onSave({
      schedule,
      settings,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Edit Scheduled Search' : 'Schedule Search'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Run every (days)"
                value={schedule.daysInterval}
                onChange={(e) => {
                  const value = Math.max(1, parseInt(e.target.value) || 1);
                  setSchedule({ ...schedule, daysInterval: value });
                }}
                inputProps={{ min: 1 }}
                helperText={`Searches will run exactly every ${schedule.daysInterval} day${schedule.daysInterval > 1 ? 's' : ''} (${schedule.daysInterval * 24} hours)`}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Search Settings
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Results after"
                value={settings.afterDate}
                onChange={(e) => setSettings({ ...settings, afterDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Only Google results posted after this date"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.deeperProcess}
                    onChange={(e) => setSettings({ ...settings, deeperProcess: e.target.checked })}
                  />
                }
                label="deeper process"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  value={settings.categories}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const lastSelected = newValue[newValue.length - 1];
                    
                    if (lastSelected === 'all') {
                      setSettings({ 
                        ...settings, 
                        categories: ['all'],
                        categoryType: 'all'
                      });
                    } else {
                      const filteredValue = newValue.filter(cat => cat !== 'all');
                      setSettings({ 
                        ...settings, 
                        categories: filteredValue,
                        categoryType: 'specific'
                      });
                    }
                  }}
                  input={<OutlinedInput label="Categories" />}
                  renderValue={(selected) => {
                    if (selected.includes('all')) {
                      return 'All Categories';
                    }
                    return selected.join(', ');
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {initialData ? 'Update' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduledSearchDialog;
